import React, { useState, useEffect } from 'react';
import {
    EcDetailsFrom,
    FieldsError,
    Phone,
    Fax,
    MultiLineFieldsName,
} from './types';
import {
    InputText,
    LoaderBtn,
    Loader,
    ExtendedSelect,
    AddButton,
    DeleteButton,
} from 'components';
import { Form, Button, Col } from 'react-bootstrap';
import { ApiErrorFailures } from '_helpers/errors/ApiError';
import { formatPhoneNumber } from '_helpers';
import styles from './EcDetails.module.scss';
import cn from 'classnames';
import _ from 'lodash';

const initDetails: EcDetailsFrom = {
    inn: '',
    ogrn: '',
    kpp: '',
    directorsName: '',
    legalAddress: '',
    bankAccountNumber: '',
    bankName: '',
    bankBIK: '',
    bankCorrespondentAccountNumber: '',
    phones: [{ number: '', internal: '' }],
    faxes: [{ number: '', internal: '' }],
    emails: [''],
    id: 'new',
    regNumber: '',
    name: '',
    fullName: '',
    city: '',
    address: '',
    website: '',
    examinationCenterRegNumber: '',
    regionId: 14,
    host: '',
    agreementWithSpkfr: '',
    agreementWithRs: '',
};

const emptyPhone: Phone = { number: '', internal: '' };
const emptyFax: Fax = { number: '', internal: '' };
const initErrors: FieldsError = {};

const EcDetails = ({
    apiErrorFailures,
    loading,
    loadingDetail,
    onSubmit,
    onCancel,
    submitBtnTitle,
    initEcDetails,
    readOnly,
    isQec,
    regions,
}: Props) => {
    const [ecDetails, setEcDetails] = useState(React.useMemo(() => initEcDetails || initDetails, [initEcDetails]));
    const [formFieldErrors, setFormFieldErrors] = useState(initErrors);
    const regionOptions = (regions || [{ id: 14, name: 'Москва' }]).map(v => ({ value: `${v.id}`, label: v.name }));
    let invalidInputRef: HTMLInputElement | null = null;

    useEffect(() => {
        if (apiErrorFailures) {
            setFormFieldErrors({
                ...formFieldErrors,
                ...apiErrorFailures,
            });
        }
    }, [apiErrorFailures]);

    useEffect(() => {
        if (invalidInputRef) {
            invalidInputRef.focus();
        }
    }, [formFieldErrors]);

    useEffect(() => {
        if (initEcDetails && Object.keys(formFieldErrors).length === 0) {
            setEcDetails(initEcDetails);
        }
    }, [initEcDetails]);

    useEffect(() => {
        invalidInputRef = null;
    }, [ecDetails]);

    const setInvalidInputRef = (node: HTMLInputElement | null) => {
        if (!invalidInputRef) {
            invalidInputRef = node;
        }
    };

    const getValidationErrors = (data: EcDetailsFrom): FieldsError => {
        const requiredErrorText = 'Обязательное поле для заполнения';

        const res = Object.keys(data).reduce((acc, key) => {
            switch (key) {
                case 'faxes':
                case 'emails':
                    return {
                        ...acc,
                        ...data.emails.reduce((y, x, i) => {
                            if (x.length === 0) {
                                return { ...y, [`email${i}`]: requiredErrorText };
                            }
                            return { ...y, [`email${i}`]: '' };
                        },
                            {}),
                    };
                case 'phones':
                    return {
                        ...acc,
                        ...data.phones.reduce((y, x, i) => {
                            if (x.number.length === 0) {
                                return { ...y, [`phone${i}`]: requiredErrorText };
                            }
                            return { ...y, [`phone${i}`]: '' };
                        },
                            {}),
                    };
                case 'examinationCenterRegNumber':
                case 'agreementWithSpkfr':
                case 'agreementWithRs':
                    return { ...acc, [key]: '' };
                default:
                    return { ...acc, [key]: data[key as keyof EcDetailsFrom] === '' && key !== 'host' ? requiredErrorText : '' };
            }
        }, {});
        return res;
    };

    const isValidFormData = (errors: FieldsError): boolean => _.values(errors).every(e => e.length === 0);

    const formSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!onSubmit) return;

        const validationErrors = getValidationErrors(ecDetails);

        if (isValidFormData(validationErrors)) {
            onSubmit(ecDetails);
            return;
        }
        setFormFieldErrors(validationErrors);
    };

    const handleChangeTextInputAsNum = (name: string, value: string) => {
        setEcDetails({ ...ecDetails, [name]: value.replace(/\D+/g, '') });
        setFormFieldErrors({ ...formFieldErrors, [name]: '' });
    };

    const handleChangeTextInput = (name: string, value: string) => {
        setEcDetails({ ...ecDetails, [name]: value });
        setFormFieldErrors({ ...formFieldErrors, [name]: '' });
    };

    const multiLineInputMappper = {
        phones: () => setEcDetails({ ...ecDetails, phones: [...ecDetails.phones, { ...emptyPhone }] }),
        faxes: () => setEcDetails({ ...ecDetails, faxes: [...ecDetails.faxes, { ...emptyFax }] }),
        emails: () => setEcDetails({ ...ecDetails, emails: [...ecDetails.emails, ''] }),
    };

    const removeContactRow = (fieldName: MultiLineFieldsName, rowNumber: number) => {
        switch (fieldName) {
            case 'phones':
                setEcDetails({ ...ecDetails, phones: ecDetails.phones.filter((x, i) => i !== rowNumber) });
                setFormFieldErrors({ ...formFieldErrors, [`phone${rowNumber}`]: '' });
                return;
            case 'faxes':
                setEcDetails({ ...ecDetails, faxes: ecDetails.faxes.filter((x, i) => i !== rowNumber) });
                return;
            case 'emails':
                setEcDetails({ ...ecDetails, emails: ecDetails.emails.filter((x, i) => i !== rowNumber) });
                setFormFieldErrors({ ...formFieldErrors, [`email${rowNumber}`]: '' });
                return;
            default:
                console.error('Row remove error!');
        }
    };

    const handleChangePhone = (phoneNumber: number, fieldName: string, value: string) => {
        setEcDetails({
            ...ecDetails,
            phones: ecDetails.phones.map((phone, i) => (i === phoneNumber ? { ...phone, [fieldName]: value } : phone)),
        });
        setFormFieldErrors({ ...formFieldErrors, [`phone${phoneNumber}`]: '' });
    };

    const handleChangeFax = (faxNumber: number, fieldName: string, value: string) => {
        setEcDetails({
            ...ecDetails,
            faxes: ecDetails.faxes.map((fax, i) => (i === faxNumber ? { ...fax, [fieldName]: value } : fax)),
        });
        setFormFieldErrors({ ...formFieldErrors, [`fax${faxNumber}`]: '' });
    };

    const handleChangeEmail = (emailNumber: number, value: string) => {
        setEcDetails({
            ...ecDetails,
            emails: ecDetails.emails.map((email, i) => (i === emailNumber ? value : email)),
        });
        setFormFieldErrors({ ...formFieldErrors, [`email${emailNumber}`]: '' });
    };

    const renderError = (message: string) => (
        <div className="row">
            <Form.Text className="col offset-1">
                <p className="nav-link m-0 p-0 text-danger">
                    {message}
                </p>
            </Form.Text>
        </div>
    );

    const renderRowBtn = (fieldName: MultiLineFieldsName, rowNumber: number, isLastRow: boolean) => (
        <div key={`phone_${rowNumber}`} className={styles.eventButtonsContainer}>
            {rowNumber !== 0 || !isLastRow ? <DeleteButton onClick={() => removeContactRow(fieldName, rowNumber)} /> : null}
            {isLastRow ? <AddButton onClick={multiLineInputMappper[fieldName]} /> : null}
        </div>
    );

    const renderPhones = () => ecDetails.phones.map((phone, i, arr) => (
        <div key={`${i}-phone`} className="position-relative">
            <InputText
              setRef={setInvalidInputRef}
              name={`phone${i}`}
              value={readOnly ? formatPhoneNumber(phone.number) || phone.number : phone.number}
              labelTitle={i === 0 ? 'ТЕЛЕФОН' : undefined}
              onChange={(name, value) => handleChangePhone(i, 'number', value)}
              labelColSize={2}
              inputColSize={6}
              inputClassNames={cn('d-flex', i > 0 && 'mt-4')}
              inputGroupClassNames={readOnly ? 'mb-4' : 'mb-0'}
              fieldFailures={formFieldErrors}
              disabled={readOnly}
              withDarkLabel
            >
                {!readOnly && renderRowBtn('phones', i, i === arr.length - 1)}
                {/* <Form.Control
                  name="internal"
                  value={phone.internal}
                  className={`ml-3 ${styles.internalNumberField}`}
                  placeholder="Добавочный"
                  onChange={(event: any) => handleChangePhone(i, event.target.name, event.target.value)}
                  disabled={readOnly}
                /> */}
            </InputText>
        </div>
    ));

    const renderFaxes = () => ecDetails.faxes.map((fax, i, arr) => (
        <div key={`${i}-fax`} className="position-relative">
            <InputText
              setRef={setInvalidInputRef}
              name={`fax${i}`}
              value={fax.number}
              labelTitle={i === 0 ? 'ФАКС' : undefined}
              onChange={(name, value) => handleChangeFax(i, 'number', value)}
              labelColSize={2}
              inputColSize={6}
              inputClassNames={cn('d-flex', i > 0 && 'mt-4')}
              inputGroupClassNames={readOnly ? 'mb-4' : 'mb-0'}
              fieldFailures={formFieldErrors}
              disabled={readOnly}
              withDarkLabel
            >
                {!readOnly && renderRowBtn('faxes', i, i === arr.length - 1)}
                {/* <Form.Control
                  name="internal"
                  value={fax.internal}
                  className={`ml-3 ${styles.internalNumberField}`}
                  placeholder="Добавочный"
                  onChange={(event: any) => handleChangeFax(i, event.target.name, event.target.value)}
                  disabled={readOnly}
                /> */}
            </InputText>
        </div>
    ));

    const renderEmails = () => ecDetails.emails.map((email, i, arr) => (
        <div key={`${i}-email`} className="position-relative">
            <InputText
              setRef={setInvalidInputRef}
              name={`email${i}`}
              value={email}
              labelTitle={i === 0 ? 'ПОЧТА' : undefined}
              onChange={(name, value) => handleChangeEmail(i, value)}
              labelColSize={2}
              inputColSize={6}
              inputClassNames={cn('d-flex', i > 0 && 'mt-4')}
              inputGroupClassNames={readOnly ? 'mb-4' : 'mb-0'}
              fieldFailures={formFieldErrors}
              disabled={readOnly}
              withDarkLabel
            >
                {!readOnly && renderRowBtn('emails', i, i === arr.length - 1)}
            </InputText>
        </div>
    ));

    const renderDefaultTextInput = (
        name: Exclude<keyof EcDetailsFrom, 'phones' | 'faxes' | 'emails' | 'regionId'>,
        title: string,
        mb: string = 'mb-4',
        asNum?: boolean,
    ) => (
            <InputText
              setRef={setInvalidInputRef}
              disabled={readOnly}
              fieldFailures={formFieldErrors}
              name={name}
              value={ecDetails[name] || ''}
              labelTitle={title}
              onChange={asNum ? handleChangeTextInputAsNum : handleChangeTextInput}
              labelColSize={2}
              inputColSize={6}
              inputGroupClassNames={mb}
              withDarkLabel
            />
        );

    const renderRegion = () => (
        <Form.Group className="row" style={{ alignItems: 'center' }}>
            <Col sm="2" className="p-0">
                <Form.Label column>
                    РЕГИОН
                </Form.Label>
            </Col>
            <Col sm="6">
                <ExtendedSelect
                  name="region"
                  value={ecDetails.regionId.toString()}
                  options={regionOptions}
                  onChange={(e) => {
                        setEcDetails({ ...ecDetails, regionId: parseInt(e.value) });
                        setFormFieldErrors({ ...formFieldErrors, regionId: '', region: '' });
                    }}
                  disabled={readOnly}
                />
                <Form.Control.Feedback type="invalid">{formFieldErrors.region || formFieldErrors.regionId}</Form.Control.Feedback>
            </Col>
        </Form.Group>
    );

    if (loadingDetail) {
        return <Loader />;
    }

    return (
        <Form onSubmit={formSubmit} className="container p-0 m-0" autoComplete="off">
            {renderDefaultTextInput('regNumber', 'РЕГ. НОМЕР')}
            {isQec ? renderDefaultTextInput('examinationCenterRegNumber', 'РЕГ. НОМЕР ЭЦ') : null}
            {renderDefaultTextInput('fullName', 'ПОЛН. НАЗВАНИЕ')}
            {renderDefaultTextInput('name', 'СОКР. НАЗВАНИЕ', 'mb-5')}

            {renderDefaultTextInput('inn', 'ИНН', 'mb-4', true)}
            {renderDefaultTextInput('kpp', 'КПП', 'mb-4', true)}
            {renderDefaultTextInput('ogrn', 'ОГРН', 'mb-5', true)}

            {renderDefaultTextInput('directorsName', 'ФИО ДИРЕКТОРА', 'mb-5')}

            {renderDefaultTextInput('legalAddress', 'ЮР. АДРЕС')}
            {renderDefaultTextInput('city', 'ГОРОД')}
            {renderRegion()}
            {renderDefaultTextInput('address', 'ФАКТ. АДРЕС', 'mb-5')}

            {renderDefaultTextInput('bankAccountNumber', 'СЧЕТ №', 'mb-4', true)}
            {renderDefaultTextInput('bankName', 'БАНК')}
            {renderDefaultTextInput('bankBIK', 'БИК', 'mb-4', true)}
            {renderDefaultTextInput('bankCorrespondentAccountNumber', 'КОР. СЧЕТ', 'mb-5', true)}

            {renderDefaultTextInput('agreementWithSpkfr', '№ ДОГ. С СПКФР', 'mb-4')}
            {isQec && renderDefaultTextInput('agreementWithRs', '№ ДОГ. С РС', 'mb-5')}

            <div className="mb-4">
                {renderPhones()}
            </div>

            <div className="mb-4">
                {renderFaxes()}
            </div>

            <div className="mb-4">
                {renderEmails()}
            </div>
            {renderDefaultTextInput('website', 'ВЕБ-САЙТ', 'mb-0')}
            {/* {initEcDetails && renderDefaultTextInput('host', 'ХОСТ', 'mb-0', false, true)} */}

            {_.has(formFieldErrors, 'general') ? renderError(formFieldErrors.general) : null}

            <div className="row">
                <div className={`${styles.buttons_container} col offset-2`}>
                    {!readOnly && (
                        <LoaderBtn type="submit" variant="primary" className={cn(styles.createBtn, 'action-button')} loading={loading}>
                            {submitBtnTitle || 'Сохранить'}
                        </LoaderBtn>
                    )}
                    {onCancel && (
                        <Button onClick={onCancel} variant="outline-secondary" className={cn(styles.cancelBtn, 'action-button')}>
                            {readOnly ? 'Вернуться' : 'Отмена'}
                        </Button>
                    )}
                </div>
            </div>
        </Form>
    );
};

export default EcDetails;

interface Props {
    apiErrorFailures: ApiErrorFailures | null;
    loading?: boolean;
    loadingDetail?: boolean;
    onSubmit?: (formData: EcDetailsFrom) => void;
    onCancel?: () => void;
    submitBtnTitle?: string;
    initEcDetails?: EcDetailsFrom;
    readOnly?: boolean;
    isQec?: boolean;
    regions?: IRegion[] | null;
}

interface IRegion {
    id: number;
    name: string;
}
